import { resourceGroups } from "../data/constants";
import validate from "validate.js";
import { ITask, IAction } from "../modules/ninVerification/types";
import { IAddress } from "../app/modules/kyc/company/interfaces/ICompanyProfile";
import moment from "moment";
import { namesFuzzyMatch } from "person-name-match";

export interface IRole {
  role: string | string[];
}
export const checkUserRole = (role: IRole, resource: string) => {
  let resourceExists = false;
  if (Array.isArray(role)) {
    resourceExists = checkRoleArray(role, resource);
  } else {
    resourceExists = checkRoleString(`${role}`, resource);
  }
  return resourceExists;
};
const checkRoleString = (role: string, resource: string) => {
  const roleItem = role.toLowerCase();
  if (resourceGroups[roleItem] && resourceGroups[roleItem].includes(resource))
    return true;
  return false;
};
const checkRoleArray = (role: string[], resource: string) => {
  for (let i = 0; i < role.length; i++) {
    const roleItem = role[i].toLowerCase();
    if (
      resourceGroups[roleItem] &&
      resourceGroups[roleItem].includes(resource)
    ) {
      return true;
    }
  }
  return false;
};

export const checkRoleAvailability = (
  role: IRole,
  availableRoles: string[]
) => {
  let resourceExists = false;
  if (Array.isArray(role)) {
    resourceExists = role.some((role: any) =>
      availableRoles.includes(role.toLowerCase())
    );
  } else {
    resourceExists = availableRoles.includes(`${role}`.toLowerCase());
  }
  return resourceExists;
};

export const tinStatusDetails = (status: any) => {
  switch (status) {
    case 'Valid':
    case 'ACTIVE':
      return { pillClass: 'badge-light-success', label: 'Active', tooltip: "Verification Successful: TIN Active" }
    case 'INVALID TIN':
      return { pillClass: 'badge-light-danger', label: 'Invalid', tooltip: "Verification failed: TIN is inactive or invalid" }
    case 'Error':
      return { pillClass: 'badge-light-danger', label: 'Not Found', tooltip: "TIN does not match any records" }
    case 'Expired':
      return { pillClass: 'badge-light-danger', label: 'Expired', tooltip: "TIN is expired" }
    case 'Stop-listed':
      return { pillClass: 'badge-light-danger', label: 'Blocked!', tooltip: "TIN is blocked or suspended to prevent unauthorized use" }
    default:
      return { pillClass: 'badge-light-warning', label: 'Pending', tooltip: "Processing still pending" }
  }
};
export const tinSummary = (errorCode?: string, description?: string) => {
  switch (errorCode) {
    case "E000":
      return {
        status: "success",
        label: "TIN verification successful",
        description: "Found",
        labelClass: "text-success",
      };

    default:
      return {
        // status: 'error',
        // label: "TIN verification failed",
        // labelClass: "text-danger",
        // description: description
      };
  }
};

export const companyStatusDetails = (status: any) => {
  switch (status) {
    case "Successful":
    case "SUCCESS":
    case 'Registered':
    case 'Active':
      return { pillClass: "badge-light-success", label: "Found", tooltip: 'Registration Number matches URSB records' };
    case 'Approved':
      return { pillClass: "badge-light-success", label: "Approved", tooltip: 'Reviewed & accepted. Business can operate legally' };
    case "Error":
    case "Not Found":
    case null:
      return { pillClass: "badge-light-danger", label: "Not Found", tooltip: "Registration number does not match any URSB records" };

    case "inProgress":
      return { pillClass: "", label: "", tooltip: "In progress. Please wait" };

    default:
      return { pillClass: "badge-light-warning", label: "Pending", tooltip: "Please wait" }
  }
};

export const cardStatusDetails = (status: any) => {
  switch (status) {
    case true:
      return { pillClass: "badge-light-success", label: "Match" };

    case false:
      return { pillClass: "badge-light-danger", label: "Mismatch" };

    default:
      return { pillClass: "", label: "" };
  }
};

export const verificationSummary = (status: any) => {
  switch (status) {
    case true:
      return 'Provided ID details are consistent with NIRA records';

    case false:
      return 'Not all the information on the ID match NIRA records';
    case null:
      return 'Information on the ID does not match NIRA records';

    case 'valid':
      return 'ID Document is authentic';

    case 'invalid':
      return 'ID Document is not authentic';

    default:
      return 'ID data was not verified';
  }
};

export const livenessDetectionDetails = (status: any) => {
  switch (status) {
    case true:
      return { pillClass: "badge-light-success", label: "Liveness Verified" };

    case false:
      return { pillClass: "badge-light-danger", label: "Liveness Failed" };

    case 'Not Processed':
      return { pillClass: "badge-dark", label: "Not Processed" };

    default:
      return { pillClass: "badge-light-dark", label: "Unavailable" };
  }
};

export const photoMatchDetails = (status: any) => {
  switch (status) {
    case true:
      return { pillClass: "badge-light-success", label: "Match" };

    case false:
      return { pillClass: "badge-light-danger", label: "No Match" };

    case 'Not Processed':
      return { pillClass: "badge-dark", label: "Not Processed" };

    default:
      return { pillClass: "badge-light-dark", label: "Unavailable" };
  }
};

export const ninStatusDetails = (status: any) => {

  if (status?.length > 30 && status?.length < 100) {
    status = 'Not Found'
  }
  switch (status) {
    case 'Ok':
      return { pillClass: 'badge-light-success', label: 'Valid', tooltip: "Success! NIN found in NIRA database" }
    case true:
      return { pillClass: 'badge-light-success', label: 'Valid', tooltip: "Success! Data retrieved" }
    case 'Not Found':
      return { pillClass: 'badge-light-danger', label: 'Not Found', tooltip: "NIN can not be found in NIRA database" }
    case false:
      return { pillClass: 'badge-light-danger', label: 'Not Found', tooltip: "ID Number cannot be found" }
    case 'deceased':
      return { pillClass: 'badge-dark', label: 'Deceased', tooltip: "Citizen is deceased" }
    case 'not citizen':
      return { pillClass: 'badge-danger', label: 'Not Citizen', tooltip: "Not a registered citizen as per NIRA" }
    default:
      return { pillClass: 'badge-light-danger', label: 'Error', tooltip: "Something wrong happened. Please try again" }
  }
}

export const cardNumberStatus = (status: any) => {
  switch (status) {
    case 'Valid':
    case 'valid':
      return { pillClass: 'badge-light-success', label: 'Valid', tooltip: "Success! Valid ID Number" }
    case 'Not Active':
      return { pillClass: 'badge-light-danger', label: 'Not Active', tooltip: "ID Number is not active" }
    case 'Not valid':
    case 'invalid':
      return { pillClass: 'badge-light-danger', label: 'Not Valid', tooltip: "ID Number is invalid" }
    case 'Error':
      return { pillClass: 'badge-light-danger', label: 'Not Found', tooltip: "ID Number does not match any records" }
    case 'Expired':
      return { pillClass: 'badge-light-danger', label: 'Expired', tooltip: "ID card is expired" }
    case 'Stop-listed':
      return { pillClass: 'badge-light-danger', label: 'Blocked!', tooltip: "ID card is blocked or suspended to prevent unauthorized use" }
    default:
      return { pillClass: 'badge-light-warning', label: 'Pending', tooltip: "Processing still pending" }
  }
};

export const companyKCCAStatus = (status: any) => {
  switch (status) {
    case 'verified success':
    case 'active':
      return { pillClass: 'badge-light-success', label: 'Active', tooltip: "Trade License still active" }
    case 'Expired':
      return { pillClass: 'badge-light-danger', label: 'Expired', tooltip: "Trade License expired" }
    case 'Error':
      return { pillClass: 'badge-light-danger', label: 'Not Found', tooltip: "Trade License can not be found" }
    default:
      return { pillClass: 'badge-light-danger', label: 'Not Found', tooltip: "Trade License can not be found" }
  }
};

export const workflowOverallStatus = (status: any) => {
  switch (status) {
    case "Closed":
      return { pillClass: "badge-light-success", label: "Closed" };

    case "Open":
      return { pillClass: "badge-light-warning", label: "Open" };

    case "Error":
      return { pillClass: "badge-light-danger", label: "Error" };

    default:
      return { pillClass: "badge-light-danger", label: "Error" };
  }
};

export const COpersonWorkflowStatus = (status: any) => {
  switch (status) {
    case "Received":
      return { pillClass: "badge-light-warning", label: "Received" };

    case "PendingApproval":
      return { pillClass: "badge-light-dark", label: "Pending Approval" };

    case "Processing":
      return { pillClass: "badge-light-dark", label: "Processing" };

    case "Declined":
      return { pillClass: "badge-light-danger", label: "Declined" };

    case "Completed":
      return { pillClass: "badge-light-success", label: "Completed" };

    default:
      return { pillClass: "badge-light-danger", label: "Pending" };
  }
};

export const phoneNumberStatus = (state: any) => {
  switch (state) {
    case 0:
    case 'Invalid':
      return { pillClass: "badge-light-danger", label: "Not Found", tooltip: "Phone number does not match any records" };

    case 1:
    case 'Valid':
      return { pillClass: "badge-light-success", label: "Found", tooltip: "Verification successful! Records indicate that this phone number is currently active" };

    case 'Unknown':
      return { pillClass: "badge-light-danger", label: "Verification Failed", tooltip: "Failed to verify phone number" };
    default:
      return { pillClass: "badge-light-danger", label: "Error", tooltip: "Something wrong happened. Please try again" };
  }
};

export const mtnPhoneNumberStatus = (state: any) => {
  switch (state) {
    case 'active':
      return { pillClass: "badge-light-success", label: "Active", tooltip: "Phone number is currently active/in use" };
    case 'inactive':
      return { pillClass: "badge-light-danger", label: "Inactive", tooltip: "Phone number is currently inactive/not in use" };
    default:
      return { pillClass: "badge-light-danger", label: "Error", tooltip: "Something wrong happened. Please try again" };
  }
};

export const isOwnerPNStatus = (status: any) => {
  switch (status) {
    case true:
      return { pillClass: "badge-light-success", label: "Match", tooltip: "Verification successful! Phone number & ID corresponds with database record" };
    case false:
      return { pillClass: "badge-light-danger", label: "Mismatch", tooltip: "Unable to find a matching record for this phone number" };
    case null:
      return { pillClass: "badge-light-danger", label: "Not Found", tooltip: "We couldn't locate this phone number & ID" };
    default:
      return { pillClass: "badge-light-danger", label: "Error", tooltip: "Something wrong happened. Please try again" };
  }
};

export const emailStatus = (state: any) => {
  switch (state) {
    case 'invalid':
      return { pillClass: "badge-light-danger", label: "Invalid", tooltip: "Email does not match any records" };
    case 'valid':
      return { pillClass: "badge-light-success", label: "Valid", tooltip: "Verification successful! Records indicate that this Email is currently active" };
    case 'unknown':
      return { pillClass: "badge-light-danger", label: "Uncertain", tooltip: "Failed to verify Email" };
    default:
      return { pillClass: "badge-light-danger", label: "Error", tooltip: "Something wrong happened. Please try again" };
  }
};

export const parseJson = (data: any) => {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (e) {
    return "";
  }
};

export const parseTaskStatus = (st: any) => {
  switch (st) {
    case 0:
      return "Pending";
    case 1:
      return "Successful";
    case -1:
      return "Error";
    default:
      return "Error";
  }
};

export const readActionOutPut = (
  taskName: string,
  tasks: Array<ITask>,
  actionList: any
) => {
  const task = tasks.filter((it) => it.name === taskName)[0];
  if (task) {
    const { actions = [] } = task;
    const reqActions = actions.filter((it) => actionList.indexOf(it.name) >= 0);
    let data = undefined;
    for (let i = 0; i < reqActions.length; i++) {
      const action = reqActions[i];
      const { outputData } = action;
      if (!validate.isEmpty(outputData)) {
        data = parseJson(outputData);
        if (data) {
          data["status"] = action.status;
          data["statusLabel"] = parseTaskStatus(action.status);
          data["statusMessage"] = action.statusMessage;
          data["runDate"] = action.runDate;
          data["id"] = action.id;
        }
        if (actionList.length === 1) {
          //This caters for cases where we can have optional actions
          break;
        }
      }
    }
    return data;
  } else {
    return undefined;
  }
};

export const readActionOutPutStatusIDV = (
  actions: Array<IAction>,
  actionName: any
) => {
  const reqActions: any = actions.filter(
    (it) => actionName.indexOf(it.name) >= 0
  );
  let data = undefined;
  if (reqActions.length >= 1) {
    if (reqActions[0].statusMessage === "Skipped Action") {
      reqActions[0].status = "Skipped";
    }

    const { status, statusMessage } = reqActions[0];
    data = { status, statusMessage };
  }
  return data;
};

export const readActionOutPutStatus = (
  actions: Array<IAction>,
  actionName: any
) => {
  const reqActions: any = actions?.find((q: any) => q.name === actionName);

  let data = undefined;
  if (reqActions) {
    if (reqActions?.outputData) {
      const outputData = JSON.parse(reqActions?.outputData);

      if (outputData?.errors) {
        return {
          status: "Error",
          statusMessage: outputData?.errors?.Tin[0],
        };
      }

      if (
        outputData?.isError ||
        outputData?.ursbErrorCode == "ENTITY_NOT_FOUND"
      ) {
        return { status: "Not Found", statusMessage: "" };
      }
    }

    if (reqActions.statusMessage === "Skipped Action") {
      reqActions.status = "Skipped";
    }

    const { status, statusMessage } = reqActions;
    data = { status, statusMessage };
  }

  return data;
};

export const readTinVerificationOutput = (actions: Array<IAction>) => {
  const outputData = actions?.find(
    (q: any) => q.name === "tin-verification-request"
  )?.outputData;
  if (outputData) {
    return JSON.parse(outputData);
  } else return undefined;
};

export const readUrsbVerificationOutput = (actions: Array<IAction>) => {
  const outputData = actions?.find(
    (q: any) => q.name === "ursb-verification-request"
  )?.outputData;
  if (outputData) {
    return JSON.parse(outputData);
  } else return undefined;
};

export const readKccaLicenseOutput = (actions: Array<IAction>) => {
  const outputData = actions?.find(
    (q: any) => q.name === "kcca-licence-request"
  )?.outputData;
  if (outputData) {
    return JSON.parse(outputData);
  } else return undefined;
};

export const getInitials = (name: string) => {
  const [firstname, lastname, othername] = name.toUpperCase().split(" ");

  const FN = firstname ? firstname?.substring(0, 1) : ''
  const LN = lastname ? lastname?.substring(0, 1) : ''
  const ON = othername ? othername?.substring(0, 1) : ''

  return `${FN}${LN}${ON}`;
};

export const splitFullName = (name: string) => {
  const nameArr = name?.split(" ")

  let fullName: any
  if (nameArr?.length) {
    const otherName = nameArr.length > 2 ? ' ' + nameArr[2] : '';

    fullName = {
      surname: nameArr[0],
      givenName: nameArr.length > 1 ? nameArr[1] + otherName : ''
    }
  }
  return fullName
};

export const splitCamelCase = (caseString: string) => {
  if (caseString) {
    return caseString.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  }
  return caseString
}

export const triggerCSVDownload = (data: Blob, fileName = "export.csv") => {
  const a = document.createElement("a");
  a.href = window.URL.createObjectURL(data);
  a.download = fileName;
  a.click();
};

export const triggerFileDownload = (data: Blob, fileName = "Export.zip") => {
  const a = document.createElement("a");
  a.href = window.URL.createObjectURL(data);
  a.download = fileName;
  a.click();
};


export const setTaskStatus = (taskStatus: any, size: any = null) => {

  switch (taskStatus) {
    case true:
    case 'ACTIVE':
      return { colorCode: 'success', icon: 'arrows/arr085', size: size ?? 'meduim', toolTip: 'Verification successful' }

    case false:
      return { colorCode: 'danger', icon: 'general/gen044', size: size ?? 'meduim', toolTip: 'Needs review' }

    case 'Edited':
    case 'hasData':
      return { colorCode: 'warning', icon: 'general/gen044', size: size ?? 'meduim', toolTip: 'Data entered but not verified' }

    case 'skipped':
    case 'error':
      return { colorCode: 'warning', icon: 'general/gen044', size: size ?? 'meduim', toolTip: 'Something went wrong' }

    case 'Pending':
    case 'inProgress':
      return { colorCode: 'danger', icon: 'inProgress', size: size ?? 'meduim' }

    case 'Missing':
      return { colorCode: 'dark', icon: 'files/fil006', size: size ?? 'small', toolTip: 'Data missing' }

    default:
      return { colorCode: 'warning', icon: 'general/gen044', size: size ?? 'default', toolTip: 'Something went wrong' }
  }
}

export const setLivenesStatus = (taskStatus: any, size: any = null) => {
  switch (taskStatus) {
    case true:
      return { colorCode: 'success', icon: 'arrows/arr085', size: size ?? 'meduim' }
    case false:
      return { colorCode: 'danger', icon: 'general/gen044', size: size ?? 'meduim' }
    case 'Missing':
      return { colorCode: 'warning', icon: 'general/gen044', size: size ?? 'meduim' }
    default:
      return { colorCode: 'warning', icon: 'general/gen044', size: size ?? 'default' }
  }
}

export const setBiometricStatus = (taskStatus: any, size: any = null) => {
  switch (taskStatus) {
    case true:
      return { colorCode: 'success', icon: 'arrows/arr085', size: size ?? 'meduim' }

    case false:
      return { colorCode: 'danger', icon: 'general/gen044', size: size ?? 'default' }

    default:
      return { colorCode: 'danger', icon: 'general/gen044', size: size ?? 'default' }
  }

}

export const formatAddress = (address: IAddress) => {
  if (!address.country && !address.district) return '-'
  return `${address.country || ''}${', ' + address.district || ''} ${address.county || ''} ${address.parish || ''} ${address.village || ''}`.toUpperCase()
}

export const NationalIDExpiryStatus = (expiryDate: Date) => {
  if (expiryDate > new Date()) {
    return 'Active'
  } else {
    return 'Expired'
  }
}

export const isExpired = (dateString: string) => {

  const expiryDate = new Date(dateString);
  const now = new Date();

  if (isNaN(expiryDate.getTime())) {
    return true;
  }

  return expiryDate < now;
}

export const riskProfileClass = (riskClass: string) => {
  switch (riskClass) {
    case 'Low':
      return { pillClass: "badge-light-success", label: "Low Risk" };

    case 'Medium':
      return { pillClass: "badge-light-warning", label: "Medium Risk" };

    case 'High':
      return { pillClass: "badge-light-danger", label: "High Risk" };

    default:
      return { pillClass: "badge-light-dark", label: "Missing Data" };
  }
}

export const bulkRequestStatus = (requestStatus: string) => {
  switch (requestStatus) {
    case 'Successful':
      return { pillClass: "badge-light-success", label: "Completed" };

    case 'Failed':
      return { pillClass: "badge-light-danger", label: "Failed" };

    case 'Pending':
      return { pillClass: "badge-light-dark", label: "Processing" };

    case 'Error':
      return { pillClass: "badge-light-danger", label: "Error" };

    default:
      return { pillClass: "badge-light-danger", label: "Error" };
  }
}

export const driverRestrictionFix = (code: string) => {

  switch (code) {
    case 'WONE':
    case 'N':
    case 'NO':
    case 'N NON':
    case '0':
    case 'MONE':
    case 'NOE':
      return 'NONE';
    default:
      return code;
  }
}

export const phoneNumberMask = [/\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/]

export const phoneNumberInbound = (phoneNumber: string) => {

  let formattedPN: string = phoneNumber.replace("+256", "")
  let newPN
  if (formattedPN.length > 0) {
    newPN = formattedPN?.match(new RegExp('.{1,3}', 'g'))?.join(" ")
  }
  return newPN
}

export const phoneNumberOutbound = (phoneNumber: string) => {

  if (phoneNumber.length > 0) {
    let newPN = phoneNumber.replaceAll(' ', '');
    return `+256${newPN}`
  }
  return phoneNumber
}

export const getPhoneNumberVerfication = (phoneNumber: string, verficationData: any) => {
  return verficationData?.find((q: any) => q.value === phoneNumber)
}

export const getPhoneNumberSwapCheck = (phoneNumber: string, verficationData: any) => {
  let S = verficationData?.find((q: any) => q.value === phoneNumber && q.vendorCode === 'STRM_02')
  if (!S?.value) {
    return
  }
  return JSON.parse(S?.response?.responsePayload)
}

export const checkProvider = (phoneNumber: string) => {
  const provider = phoneNumber.match(/2567[6-9]\d{3}\d{4}$/)
  if (provider) {
    return true
  }
  return false
}

export const getEmailVerfication = (email: string, verficationData: any) => {
  if (!email) return
  return verficationData?.find((q: any) => q.value === email)
}

export const verificationTypeDownload = (type: any) => {
  switch (type) {
    case 'IDV':
      return 'ID Verifications'
    case 'PNV':
      return 'Phone Number Verifications'
    case 'AMLPEP':
      return 'AML-PEP Verifications'
    case 'CO':
      return 'Person Applications'
    case 'BNV':
      return 'Company Searches'
    case 'KCCA':
      return 'KCCA Trade License Verifications'
    case 'AML':
      return 'AML Screenings'
    case 'TIN':
      return 'TIN Verifications'
    case 'BULKPERSONKYC':
      return 'Bulk Verifications Export'
    default:
      return 'File'
  }
}

export const documentTypeToReadable = (type: any) => {
  switch (type) {
    case 'customerSignature':
    case 'Customer Signature':
      return 'Signature'
    case 'passportPhoto':
    case 'Passport Photo':
      return 'Photo'
    case 'nationalId':
    case 'nationalid':
      return 'National ID'
    case 'proofOfEmployment':
      return 'Employment'
    case 'passport':
      return 'Passport'
    case 'drivingLicence':
    case 'driving-licence':
      return 'Driving Licence'
    default:
      return 'Document'
  }
}

export const calculateDaysPast = (initialDate: any) => {
  if (!initialDate) {
    return null
  }
  var ago = moment.duration(moment().diff(initialDate)).humanize() + " ago";
  return ago
}

export const personAge = (dateOfBirth: string) => {
  dateOfBirth = moment(dateOfBirth, ['DD.MM.YYYY', 'YYYY-MM-DD']).format("YYYY-MM-DD")
  const years = moment().diff(dateOfBirth, 'years');
  if (!years) return
  return `— ${years}yrs`
}

export const checkExpiryDate = (expiryDate: any) => {
  if (!expiryDate) {
    return null
  }

  expiryDate = moment(expiryDate, ['DD.MM.YYYY', 'YYYY-MM-DD']).format("YYYY-MM-DD")
  const E = moment(expiryDate).isAfter(new Date())

  if (E) {
    return false
  } else return true
}

export const checkIfMinor = (dateOfBirth: any) => {
  dateOfBirth = moment(dateOfBirth, ['DD.MM.YYYY', 'YYYY-MM-DD']).format("YYYY-MM-DD")
  const years = moment().diff(dateOfBirth, 'years');
  if (years < 18) {
    return true
  }
  return false
}

export const humanReadbleFileSize = (bytes: number): `${number} ${'B' | 'KB' | 'MB' | 'GB' | 'TB'}` => {
  const index = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Number((bytes / Math.pow(1024, index)).toFixed(2)) * 1} ${(['B', 'KB', 'MB', 'GB', 'TB'] as const)[index]}`;
};

export const notificationType = (st: any) => {
  switch (st) {
    case 0:
      return "Feature Release";
    case 1:
      return "Maintenance";
    case 2:
      return "Alert";
    case 3:
      return "Reminder";
    case 4:
      return "Update";
    default:
      return "Default";
  }
}

export const validationStepsStateClass = (status: any) => {
  switch (status) {
    case 'pending':
      return 'text-secondary';
    case 'inProgress':
      return 'spinner-border spinner-border-sm align-middle ms-2 text-dark';
    case 'done':
      return 'text-success';
    case 'failed':
      return 'text-danger';
    default:
      return '';
  }
};

export const formatRoleNames = (role: string) => {
  switch (role) {
    case 'checker':
      return 'Checker'
    case 'maker':
      return 'Maker'
    // case 'default-roles-streamline-test-realm':
    //   return 'Portal Administrator'

    default:
      return ''
  }

}
export const compareNames = (setOne: any, setTwo: any) => {
  if (!setOne || !setTwo) {
    return false
  }

  let res = namesFuzzyMatch(setOne, setTwo);
  const averageScore: number = res?.averageScore || 0
  return (averageScore < 0.5)
}

export const compareStrings = (str1: string, str2: string): boolean => {
  const MATCH_THRESHOLD = 0.6;
  const tokenize = (text: string): Set<string> => {
    const words = text
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .split(/\s+/)            // Split by whitespace
      .filter(word => word.length > 0);
    return new Set(words);
  };

  const setA = tokenize(str1);
  const setB = tokenize(str2);

  if (setA.size === 0 || setB.size === 0) {
    return setA.size === setB.size;
  }

  // Intersection: words found in both sets
  const intersectionSize = Array.from(setA).filter(word => setB.has(word)).length;

  // Union: total unique words across both sets
  const unionSize = new Set(Array.from(setA).concat(Array.from(setB))).size;

  const similarityScore = intersectionSize / unionSize;

  return similarityScore >= MATCH_THRESHOLD;
}

export const documentCode = (code: string) => {

  switch (code) {
    // Ordinary / Private Passports
    case 'P':
    case 'PA':
    case 'PB':
    case 'PC':
    case 'PP':
    case 'PQ':
      return ("Ordinary Passport (Personal)");
      break;

    // Diplomatic Passports
    case 'D':
    case 'PD':
      return ("Diplomatic Passport (High-level Officials)");
      break;

    // Service / Official Passports
    case 'S':
    case 'PS':
    case 'PO':
      return ("Service/Official Passport (Govt Business)");
      break;

    // Travel Documents / Laissez-Passer
    case 'IP':
      return ("International Passport (UN/Refugee)");
      break;

    // Emergency or Temporary Documents
    case 'PT':
      return ("Temporary/Emergency Travel Document");
      break;

    default:
      return ("Code not recognized: Likely a non-standard or regional document.");
  }
}